/**
 * RTWO Event Bus — Subscriber Registry Service
 */

import { EVENT_BUS_SERVICE_IDS } from '../domain-models';
import type { RTWOSubscriber, RTWOSubscriberStatus, SubscriberId, TopicId } from '../domain-models';
import type { ISubscriberRegistryService, SubscriberRegistryResult } from '../service-interfaces';
import type { EBusRepositories } from '../repositories/ebus-repositories';
import type { EngineSourceAdapterRegistry } from '../registry/adapter-registries';
import { EBusInternalEventTypes } from '../events/ebus-internal-event-bus';
import { BaseEBusService, type EBusServiceContext } from './base-ebus-service';

export class SubscriberRegistryService
  extends BaseEBusService
  implements ISubscriberRegistryService
{
  readonly serviceId = EVENT_BUS_SERVICE_IDS.SUBSCRIBER_REGISTRY;
  readonly serviceName = 'Subscriber Registry';
  readonly description =
    'Registry of active event subscribers via engine source adapter. No subscribers registered.';
  readonly integrations = ['All RTWO Engines', 'Topic Registry', 'Routing Engine'];
  readonly requiresRealEventSources = true;

  constructor(
    context: EBusServiceContext,
    private readonly repositories: EBusRepositories,
    private readonly engineSourceRegistry: EngineSourceAdapterRegistry
  ) {
    super(context);
  }

  async registerSubscriber(
    moduleId: string,
    subscribedTopics: TopicId[]
  ): Promise<SubscriberRegistryResult> {
    if (!this.engineSourceRegistry.isConfigured()) {
      return {
        success: false,
        subscriberId: null,
        error:
          'No Subscribers Registered — Cannot register subscribers until real RTWO engine connections are established.',
        isPlaceholder: true,
        source: 'Event Bus — No Subscribers Registered',
      };
    }

    const adapterResult = await this.engineSourceRegistry
      .getActiveAdapter()
      .registerSubscriber(moduleId, subscribedTopics);

    if (adapterResult?.subscriber) {
      this.repositories.subscribers.save(adapterResult.subscriber);
      this.context.eventBus.emit(EBusInternalEventTypes.SUBSCRIBER_REGISTERED, {
        subscriberId: adapterResult.subscriber.subscriberId,
        moduleId,
      });
    }

    return {
      success: false,
      subscriberId: null,
      error:
        'No Subscribers Registered — Cannot register subscribers until real RTWO engine connections are established.',
      isPlaceholder: true,
      source: 'Event Bus — No Subscribers Registered',
    };
  }

  getAllSubscribers(): RTWOSubscriber[] {
    if (!this.engineSourceRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.subscribers.findAll();
  }

  getSubscriber(subscriberId: SubscriberId): RTWOSubscriber | null {
    if (!this.engineSourceRegistry.isConfigured()) {
      return null;
    }

    return this.repositories.subscribers.findById(subscriberId);
  }

  getSubscribersByStatus(status: RTWOSubscriberStatus): RTWOSubscriber[] {
    if (!this.engineSourceRegistry.isConfigured()) {
      return [];
    }

    return this.repositories.subscribers.findByStatus(status);
  }

  getActiveSubscriberCount(): 0 {
    return 0;
  }
}
